import React, {
    forwardRef,
    FunctionComponent,
    Suspense,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { clone, forEach, get, has, isFunction, isNull, isUndefined, map, merge } from 'lodash';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { getKeyPhrases } from 'admin/key-phrases/KeyPhrases';
import { isAuthorized } from 'auth/Auth';
import { EmojiPicker } from 'common/emojis/emoji-picker/EmojiPicker';
import { Gallery } from 'common/gallery/Gallery';
import { MentionPicker } from 'common/mentions/mention-picker/MentionPicker';
import { AudioRecorder } from 'common/recorder/audio-recorder/AudioRecorder';
import { VideoRecorder } from 'common/recorder/video-recorder/VideoRecorder';
import Editor from 'common/response-editor/editor/Editor';
import { MenuBar, Props as MenuBarProps, ResponseChannelsInfo } from 'common/response-editor/menu-bar/MenuBar';
import { ResponseChannel } from 'common/response-editor/menu-bar/MenuBar.types';
import { MetaData } from 'common/response-editor/MetadataParser';
import {
    ToolBar,
    ToolBarOptions,
    ToolBarTextOptions,
    defaultSpecialButtonsOptions,
    RespondButtonType,
    RespondButtonSplitOption,
} from 'common/response-editor/tool-bar/ToolBar';
import { TemplatePicker } from 'common/templates/template-picker/TemplatePicker';
import { Uploader } from 'common/upload/uploader/Uploader';
import { VariablePicker } from 'common/variables/variable-picker/VariablePicker';
import { Overlay } from 'core/overlay/Overlay';
import { LocaleCode } from 'core/util/Locale';
import { Color } from 'core/util/Variables';
import { EmailSender } from 'surveys/common/types/SurveyResponse';

import 'react-quill/dist/quill.snow.css';
import 'common/response-editor/ResponseEditor.scss';
import { VobMetadata } from './vob/VobMetadata.types';
import { WarningMessage } from './warning-message/WarningMessage';

export interface SubmitResponse {
    isValid: boolean;
    errorMsg?: string;
}

interface ResponseEditorHandlers {
    onTextChange?: any;
    onResponseSubmit?: (
        response: any,
        metadata?: VobMetadata,
        evaluationResponse?: any,
    ) => Promise<SubmitResponse | void> | void;
    onReadOnlyClick?: any;
    onChannelChange?: any;
    onFocus?: () => void;
    onBlur?: () => void;
    /** common cb for all media updates, for specific callbacks (on add, delete, re-order, ...) check mediaOptions */
    onMediaChange?: any;
    onEditorClick?: any;
    onCancelClick?: any;
    onVobThresholdMetUpdate?: any;
}

export enum TextFormat {
    Text = 'text',
    HTML = 'html',
    Quill = 'quill',
}

interface TextOptions extends ToolBarTextOptions {
    defaultText?: MetaData[];
    twitterSelected?: boolean;
}

export enum ResponseEditorWidgetPlacement {
    Top = 'top',
    Bottom = 'bottom',
}

export const EditorOverlayArea = 'editor-overlay-area';

interface ReadOnlyOptions {
    enable: boolean;
    icon?: string;
    text?: string;
    isClickable?: boolean;
    backgroundColor?: string;
    CustomElement?: FunctionComponent<any>;
    customElementProps?: any;
}

interface SuggestedResponseOptions {
    enable: boolean;
    getData?: (responseEditorId: string) => Promise<string[]>;
    data?: string[];
}

interface ProfanityOptions {
    enable: boolean;
    customGetProfanityPhrases?: any;
    CustomWarningElement?: FunctionComponent<{ profanePhrases: string[] }>;
}

export interface Props {
    agencyId: number;
    tenantId: number;
    userLocale?: LocaleCode;
    customerID?: string;
    id?: string;
    placeholder?: string;
    /** default: true */
    focus?: boolean;
    textOptions?: TextOptions;
    toolBarOptions?: ToolBarOptions;
    menuBarOptions?: MenuBarProps;
    readOnlyOptions?: ReadOnlyOptions;
    suggestedResponseOptions?: SuggestedResponseOptions;
    profanityOptions?: ProfanityOptions;
    handlers?: ResponseEditorHandlers;
    enableRespondOnEnterKey?: boolean;
    widgetPlacement?: ResponseEditorWidgetPlacement;
    /** default: true */
    canEdit?: boolean;
    reviewId?: string;
    reviewCreatedAt?: string;
    locationId?: number;
    userId?: number;
    vobReviewResponseEnabled?: boolean;
    showResponseOptions?: boolean;
    generateAIResponseOnLoad?: boolean;
    isNorthStarEnabled?: boolean;
}

const getWidgetStyles = ({ actionContainerRef, editorRef, widgetPlacement, searchStartIndex }: any): object => {
    const positionStyles: any = {};

    if (actionContainerRef && actionContainerRef.current && editorRef && editorRef.current) {
        const index = editorRef.current.getSelection();
        const bounds = editorRef.current.getBounds(index - 1);
        const WidgetWidth = 400;

        if (isNull(searchStartIndex)) {
            if (widgetPlacement === ResponseEditorWidgetPlacement.Top) {
                positionStyles.bottom = actionContainerRef.current.clientHeight;
            } else {
                positionStyles.top = actionContainerRef.current.clientHeight;
            }
        } else {
            const { clientWidth } = actionContainerRef.current;

            if (widgetPlacement === ResponseEditorWidgetPlacement.Top) {
                positionStyles.bottom = bounds.bottom + 5;
            } else {
                positionStyles.top = bounds.bottom + 5;
            }
            if (clientWidth - WidgetWidth > bounds.left) {
                positionStyles.left = bounds.left;
            } else {
                positionStyles.right = 5;
            }
        }
    }
    return positionStyles;
};

const Widgets = ({
    agencyId,
    tenantId,
    dropdownRef,
    actionContainerRef,
    editorRef,
    handleCustomWidgetValueSelect,
    handleCustomWidgetClose,
    handleDataLoad,
    showTemplatePicker,
    showVariablePicker,
    showMentionPicker,
    showEmojiPicker,
    showHashTagPicker,
    toolBarOptions,
    widgetPlacement,
}: {
    agencyId: number;
    tenantId: number;
    dropdownRef: any;
    actionContainerRef: any;
    editorRef: any;
    handleCustomWidgetValueSelect: any;
    handleCustomWidgetClose: any;
    handleDataLoad: any;
    showTemplatePicker: boolean;
    showVariablePicker: boolean;
    showMentionPicker: boolean;
    showEmojiPicker: boolean;
    showHashTagPicker: boolean;
    toolBarOptions: ToolBarOptions;
    widgetPlacement: ResponseEditorWidgetPlacement;
}): JSX.Element | null => {
    let widgetRender: JSX.Element | null = null;
    let searchStartIndex: null | number = null;
    let searchString = '';
    let variableEditForm = null;

    if (editorRef && editorRef.current) {
        searchStartIndex = editorRef.current.getSearchStartIndex();
        searchString = editorRef.current.getSearchString();
        variableEditForm = editorRef.current.getTemplateUrl();
    }

    if (showTemplatePicker) {
        widgetRender = (
            <TemplatePicker
                {...merge(
                    { ...toolBarOptions?.specialOptions?.templateOptions },
                    {
                        agencyId,
                        tenantId,
                        autoFocus: isNull(searchStartIndex),
                        enableAutoSelect: isNull(searchStartIndex),
                        displayOptions: {
                            reviseMaxHeight: widgetPlacement === ResponseEditorWidgetPlacement.Top,
                        },
                        searchOptions: {
                            enable: isNull(searchStartIndex),
                            defaultSearchText: searchString,
                            closeWhenNoMatch: defaultSpecialButtonsOptions.Template.closeWhenNoMatch,
                        },
                        handlers: {
                            onSelect: (value: any): void => {
                                handleCustomWidgetValueSelect(value, defaultSpecialButtonsOptions.Template.id);
                            },
                            onClose: handleCustomWidgetClose,
                            onDataLoad: handleDataLoad,
                        },
                    },
                )}
                dropdownRef={dropdownRef}
            />
        );
    } else if (showVariablePicker) {
        widgetRender = (
            <VariablePicker
                {...merge(
                    { ...toolBarOptions?.specialOptions?.variableOptions },
                    {
                        agencyId,
                        tenantId,
                        autoFocus: isNull(searchStartIndex),
                        displayOptions: {
                            reviseMaxHeight: widgetPlacement === ResponseEditorWidgetPlacement.Top,
                        },
                        variableEditForm,
                        searchOptions: {
                            enable: isNull(searchStartIndex),
                            defaultSearchText: searchString,
                            closeWhenNoMatch: defaultSpecialButtonsOptions.Variable.closeWhenNoMatch,
                        },
                        handlers: {
                            onSelect: (value: any): void => {
                                handleCustomWidgetValueSelect(value, defaultSpecialButtonsOptions.Variable.id);
                            },
                            onClose: handleCustomWidgetClose,
                            onDataLoad: handleDataLoad,
                        },
                    },
                )}
                dropdownRef={dropdownRef}
            />
        );
    } else if (showMentionPicker) {
        widgetRender = (
            <MentionPicker
                {...merge(
                    { ...toolBarOptions!.specialOptions!.mentionOptions },
                    {
                        autoFocus: isNull(searchStartIndex),
                        displayOptions: {
                            reviseMaxHeight: widgetPlacement === ResponseEditorWidgetPlacement.Top,
                        },
                        searchOptions: {
                            enable: isNull(searchStartIndex),
                            defaultSearchText: searchString,
                            closeWhenNoMatch: defaultSpecialButtonsOptions.Mention.closeWhenNoMatch,
                        },
                        handlers: {
                            onSelect: (value: any): void => {
                                handleCustomWidgetValueSelect(value, defaultSpecialButtonsOptions.Mention.id);
                            },
                            onClose: handleCustomWidgetClose,
                            onDataLoad: handleDataLoad,
                            onSearchTextChange:
                                toolBarOptions!.specialOptions!.mentionOptions!.handlers!.onSearchTextChange,
                            onOpen: toolBarOptions!.specialOptions!.mentionOptions!.handlers!.onOpen,
                            onFetchMore: toolBarOptions!.specialOptions!.mentionOptions!.handlers!.onFetchMore,
                        },
                    },
                )}
                dropdownRef={dropdownRef}
            />
        );
    } else if (showEmojiPicker) {
        widgetRender = (
            <EmojiPicker
                {...merge(
                    { ...toolBarOptions?.specialOptions?.emojiOptions },
                    {
                        autoFocus: isNull(searchStartIndex),
                        displayOptions: {
                            reviseMaxHeight: widgetPlacement === ResponseEditorWidgetPlacement.Top,
                        },
                        searchOptions: {
                            enable: isNull(searchStartIndex),
                            defaultSearchText: searchString,
                            closeWhenNoMatch:
                                !isNull(searchStartIndex) && defaultSpecialButtonsOptions.Emoji.closeWhenNoMatch,
                        },
                        handlers: {
                            onSelect: (value: any): void => {
                                handleCustomWidgetValueSelect(value, defaultSpecialButtonsOptions.Emoji.id);
                            },
                            onClose: handleCustomWidgetClose,
                            onDataLoad: handleDataLoad,
                        },
                    },
                )}
                dropdownRef={dropdownRef}
            />
        );
    } else if (showHashTagPicker) {
        widgetRender = (
            <MentionPicker
                {...merge(
                    { ...toolBarOptions!.specialOptions!.hashTagOptions },
                    {
                        autoFocus: isNull(searchStartIndex),
                        displayOptions: {
                            reviseMaxHeight: widgetPlacement === ResponseEditorWidgetPlacement.Top,
                        },
                        searchOptions: {
                            enable: isNull(searchStartIndex),
                            defaultSearchText: searchString,
                            closeWhenNoMatch: defaultSpecialButtonsOptions.HashTags.closeWhenNoMatch,
                        },
                        handlers: {
                            onSelect: (value: any): void => {
                                handleCustomWidgetValueSelect(value, defaultSpecialButtonsOptions.HashTags.id);
                            },
                            onClose: handleCustomWidgetClose,
                            onDataLoad: handleDataLoad,
                            onSearchTextChange:
                                toolBarOptions!.specialOptions!.hashTagOptions!.handlers!.onSearchTextChange,
                            onOpen: toolBarOptions!.specialOptions!.hashTagOptions!.handlers!.onOpen,
                        },
                    },
                )}
                dropdownRef={dropdownRef}
            />
        );
    }
    let widgetStyles: any = {};

    if (widgetRender) {
        widgetStyles = getWidgetStyles({
            actionContainerRef,
            editorRef,
            dropdownRef,
            widgetPlacement,
            searchStartIndex,
        });
    }
    return (
        <div className="response-editor-widgets" style={widgetStyles}>
            <Suspense fallback={null}>{widgetRender}</Suspense>
        </div>
    );
};

const ReadOnly = ({
    readOnlyOptions,
    handleReadOnly,
    t,
}: {
    readOnlyOptions: ReadOnlyOptions;
    handleReadOnly: any;
    t: any;
}): JSX.Element | null => {
    if (readOnlyOptions?.enable) {
        return (
            <>
                {readOnlyOptions.CustomElement && (
                    <readOnlyOptions.CustomElement {...readOnlyOptions.customElementProps} />
                )}
                {!readOnlyOptions.CustomElement && (
                    <div
                        role="button"
                        tabIndex={0}
                        className={`response-editor-read-only-container ${
                            readOnlyOptions?.isClickable ? 'clickable' : ''
                        }`}
                        style={{
                            backgroundColor: readOnlyOptions?.backgroundColor,
                        }}
                        onClick={handleReadOnly}
                        onKeyPress={(): void => {
                            // Do Nothing
                        }}
                    >
                        <i className={readOnlyOptions?.icon} />
                        <span className="read-only-text">{t(readOnlyOptions?.text)}</span>
                    </div>
                )}
            </>
        );
    }
    return null;
};

const EditorWarning = ({ warningText }: { warningText: string }): JSX.Element | null => {
    return (
        <Alert variant="warning" className="editor-warning">
            <i className="icon-exclamation-triangle" />
            <span>{warningText}</span>
        </Alert>
    );
};

const ProfanityWarning = ({
    profanePhrases,
    t,
    CustomWarningElement,
}: {
    profanePhrases: string[];
    t: any;
    CustomWarningElement: any;
}): JSX.Element | null => {
    let renderer = null;

    if (profanePhrases && profanePhrases.length) {
        if (CustomWarningElement) {
            renderer = <CustomWarningElement profanePhrases={profanePhrases} />;
        } else {
            renderer = (
                <EditorWarning warningText={t('WARNING_GUIDELINE_PHRASES', { phrases: profanePhrases.join(', ') })} />
            );
        }
    }
    return renderer;
};

const initializeMetadata = (): VobMetadata => {
    return {
        interaction_start_timestamp: new Date().toISOString(),
        response_publish_timestamp: new Date().toISOString(),
        copy_paste_event: false,
        template_response: false,
        user_generated_manual_response: true,
        reputation_vob_ai_generated_response: false,
        publish_anyways_cancel_count: 0,
        response_vob_generated_button_count: 0,
        response_vob_enhanced_button_count: 0,
        response_vob_generate_count: 0,
        response_vob_enhance_count: 0,
        response_vob_analyze_count: 0,
        response_vob_end_score: 0,
        vob_score_object: undefined,
    };
};

export const ResponseEditor = forwardRef<any, Props>(
    (
        {
            agencyId,
            tenantId,
            userLocale,
            id,
            placeholder,
            textOptions,
            menuBarOptions,
            toolBarOptions,
            readOnlyOptions,
            suggestedResponseOptions,
            profanityOptions,
            handlers,
            widgetPlacement,
            enableRespondOnEnterKey,
            focus,
            canEdit,
            reviewId,
            reviewCreatedAt,
            locationId,
            userId,
            vobReviewResponseEnabled,
            showResponseOptions = true,
            generateAIResponseOnLoad,
            isNorthStarEnabled,
        }: Props,
        ref: any,
    ) => {
        const { t } = useTranslation('common/response-editor');
        let sanitizedId;

        if (id && (id.includes('e+') || id.includes('E+'))) {
            sanitizedId = Number(id);
        } else {
            sanitizedId = id;
        }
        const toolBarId = `response-editor-tool-bar-${sanitizedId}`;
        const mergedMenuBarOptions = merge({}, clone(ResponseEditor.defaultProps?.menuBarOptions), menuBarOptions);
        const mergedTextOptions = merge({}, clone(ResponseEditor.defaultProps?.textOptions), textOptions);
        const mergedReadOnlyOptions = merge({}, clone(ResponseEditor.defaultProps?.readOnlyOptions), readOnlyOptions);
        const mergedToolBarOptions = merge({}, clone(ResponseEditor.defaultProps?.toolBarOptions), toolBarOptions);

        const [charCount, setCharCount] = useState<number>(0);
        const [activeChannelIndex, setActiveChannelIndex] = useState<number>(
            get(mergedMenuBarOptions, `activeResponseChannelIndex`, 0),
        );
        const [isPrivate, setIsPrivate] = useState(get(mergedMenuBarOptions, `privateOptions.checked`, false));
        const [showTemplatePicker, setshowTemplatePicker] = useState(false);
        const [showVariablePicker, setShowVariablePicker] = useState(false);
        const [showMentionPicker, setShowMentionPicker] = useState(false);
        const [showEmojiPicker, setShowEmojiPicker] = useState(false);
        const [showHashTagPicker, setShowHashTagPicker] = useState(false);
        const [showHideVideoRecord, setShowHideVideoRecord] = useState(false);
        const [showHideAudioRecord, setShowHideAudioRecord] = useState(false);
        const [media, setMedia] = useState<any[]>(mergedToolBarOptions!.specialOptions!.mediaOptions!.media! || []);
        const [isMediaUploadInProgress, setIsMediaUploadInProgress] = useState<boolean>(false);
        const [mediaEditIndex, setMediaEditIndex] = useState<number | null>(null);
        const [, setReloadWidget] = useState<object>({});
        const [suggestedResponses, setSuggestedResponses] = useState<string[]>([]);
        const [profanePhrases, setProfanePhrases] = useState<string[]>([]);
        const [editorWarning, setEditorWarning] = useState('');
        const [selectedOption, setSelectedOption] = useState<RespondButtonSplitOption>();
        const [showWarning, setShowWarning] = useState(false);
        const [meetsThreshold, setMeetsThreshold] = useState<boolean | undefined>(undefined);
        const [hasInteracted, setHasInteracted] = useState<boolean>(false);
        const defaultSelected = isAuthorized({
            actions: [ActionId.Survey3Responses.RespondOptionDefaultToUser],
        })
            ? EmailSender.USER
            : EmailSender.LOCATION;
        const [responseOptionValue, setResponseOptionValue] = useState<string>(defaultSelected);
        const [loading, setLoading] = useState<boolean>(false);

        const actionContainerRef = useRef<any>(null);
        const innerUploaderRef = useRef<any>(null);
        const dropdownRef = useRef<any>(null);
        const editorRef = useRef<any>(null);
        const metadata = useRef<VobMetadata>(initializeMetadata());
        const isTextAnalyzed = useRef<boolean>(true);

        const activeChannel = get(mergedMenuBarOptions, `responseChannelsData[${activeChannelIndex}]`, null);

        const handlePaste = (): void => {
            metadata.current.copy_paste_event = true;
        };
        const [validationError, setValidationError] = useState(false);

        useImperativeHandle(ref, () => ({
            respond: (): void => {
                callRespond();
            },
            clear: (): void => {
                resetEditor();
            },
            setContents: (metaData: any): void => {
                setTimeout(() => {
                    editorRef?.current?.setContents(metaData);
                }, 10);
            },
            getText: (): string => {
                if (editorRef.current) {
                    const { text } = editorRef.current?.getTextInfo();

                    return text;
                }

                return '';
            },
            setMedia: (newMedia: any): void => {
                if (newMedia) {
                    if (innerUploaderRef && innerUploaderRef.current) {
                        innerUploaderRef.current.clearFiles();
                        innerUploaderRef.current.addAcceptedFiles(newMedia);
                    }
                }
            },
            setActiveChannel: (index: number): void => {
                if (index !== activeChannelIndex) {
                    setActiveChannelIndex(index);
                }
            },
            setFocus: (): void => {
                if (editorRef && editorRef.current) {
                    editorRef.current.setFocus();
                }
            },
            clearFocus: (): void => {
                if (editorRef && editorRef.current) {
                    editorRef.current.setBlur();
                }
            },
            cancelUpload: (): void => {
                if (innerUploaderRef && innerUploaderRef.current) {
                    innerUploaderRef.current.cancelUpload();
                }
            },
            checkProfanity: async (): Promise<void> => {
                const { text } = editorRef.current.getTextInfo();
                const bannedWords = await checkProfanity(text);

                setProfanePhrases(bannedWords);
            },
        }));

        useEffect(() => {
            (async (): Promise<void> => {
                if (suggestedResponseOptions && suggestedResponseOptions.enable) {
                    if (suggestedResponseOptions.data! && suggestedResponseOptions.data!.length) {
                        setSuggestedResponses(suggestedResponseOptions.data!);
                    } else if (suggestedResponseOptions.getData) {
                        const suggestedResponseData = await suggestedResponseOptions.getData(id!);

                        setSuggestedResponses(suggestedResponseData!);
                    }
                }
            })();
        }, [id, suggestedResponseOptions]);

        const callRespond = (): void => {
            const text = editorRef.current.getTextInfo()?.text?.trim();

            if (!text || text.length === 0) {
                setValidationError(true);
                return;
            }

            setValidationError(false);
            editorRef.current.respond();
        };

        const handleManualTyping = (): void => {
            metadata.current.user_generated_manual_response = true;
            metadata.current.template_response = false;
            metadata.current.reputation_vob_ai_generated_response = false;
        };

        const updateMetadataButtonCount = (enhance: boolean): void => {
            if (enhance) {
                metadata.current.response_vob_enhanced_button_count += 1;
            } else {
                metadata.current.response_vob_generated_button_count += 1;
            }
        };

        const callGenerate = (enhance: boolean): void => {
            updateMetadataButtonCount(enhance);
            trackPromise(
                editorRef.current.generateAIResponse(
                    tenantId,
                    reviewId,
                    reviewCreatedAt,
                    locationId,
                    userId,
                    userLocale,
                    (vobData: any) => {
                        metadata.current.vob_score_object = vobData;
                        metadata.current.reputation_vob_ai_generated_response = true;
                        metadata.current.template_response = false;
                        metadata.current.user_generated_manual_response = false;
                        if (enhance) {
                            metadata.current.response_vob_enhance_count += 1;
                        } else {
                            metadata.current.response_vob_generate_count += 1;
                        }
                        if (handlers && handlers!.onVobThresholdMetUpdate) {
                            handlers!.onVobThresholdMetUpdate(vobData.meets_threshold);
                        }
                    },
                ),
                `${EditorOverlayArea}-${id}`,
            );
        };

        const callAnalyze = (): void => {
            editorRef.current.analyzeResponse(
                tenantId,
                reviewId,
                reviewCreatedAt,
                locationId,
                userId,
                (score: number, vobData: any) => {
                    metadata.current.response_vob_analyze_count += 1;
                    metadata.current.response_vob_end_score = score;
                    metadata.current.vob_score_object = vobData;
                    if (handlers && handlers!.onVobThresholdMetUpdate) {
                        handlers!.onVobThresholdMetUpdate(vobData.meets_threshold);
                    }
                },
            );
        };

        const handleCustomWidgetClose = (): void => {
            setshowTemplatePicker(false);
            setShowVariablePicker(false);
            setShowMentionPicker(false);
            setShowEmojiPicker(false);
            setShowHashTagPicker(false);
            editorRef.current.setSearchStartIndex(null);
            editorRef.current.setSearchString('');
            editorRef.current.setTemplateUrl({});
        };

        const handleCustomWidgetValueSelect = (value: any, widgetName: string): void => {
            editorRef.current.handleCustomWidgetValueSelect(value, widgetName);
            toolBarOptions?.specialOptions?.mentionOptions?.handlers?.onSelect({ value, widgetName });
        };

        const handleWidgetDataLoad = (): void => {
            if (editorRef && editorRef.current) {
                const searchStartIndex = editorRef.current.getSearchStartIndex();
                const searchString = editorRef.current.getSearchString();

                editorRef.current.setSelection(searchStartIndex! + searchString.length);
            }
        };

        const checkCharCount = useCallback((): boolean => {
            let isValid = true;

            if (!charCount) {
                isValid = false;
            } else if (mergedTextOptions!.minCharLimit && charCount < mergedTextOptions!.minCharLimit!) {
                isValid = false;
            } else if (mergedTextOptions!.maxCharLimit && charCount > mergedTextOptions!.maxCharLimit!) {
                isValid = false;
            }
            return isValid;
        }, [mergedTextOptions, charCount]);

        const checkProfanity = useCallback(
            async (text: string): Promise<string[]> => {
                const finalProfanePhrases = new Set<string>();

                if (profanityOptions && profanityOptions.enable) {
                    let keyPhrases: any = [];

                    if (profanityOptions.customGetProfanityPhrases) {
                        keyPhrases = await trackPromise(
                            profanityOptions.customGetProfanityPhrases(text),
                            `${EditorOverlayArea}-${id}`,
                        );
                    } else {
                        keyPhrases = await trackPromise(getKeyPhrases(tenantId, text), `${EditorOverlayArea}-${id}`);
                    }
                    forEach(keyPhrases, (phrase) => {
                        if (!finalProfanePhrases.has(phrase.keyPhraseText)) {
                            finalProfanePhrases.add(phrase.keyPhraseText);
                        }
                    });
                    console.log({ finalProfanePhrases });
                }
                return Array.from(finalProfanePhrases);
            },
            [id, profanityOptions, tenantId],
        );

        const handleMediaRemove = (index: number, allFiles?: any[]): void => {
            setMedia(allFiles!);
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.removeFile(index);
            }
            if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaDelete) {
                mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaDelete(index, allFiles);
            }
            if (handlers && handlers!.onMediaChange) {
                handlers!.onMediaChange(allFiles);
            }
        };

        const handleMediaClick = (index: number): void => {
            if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaClick) {
                mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaClick(index);
            }
        };

        const handleMediaAdded = (currentFiles: any[], allFiles: any[]): void => {
            if (!isNull(mediaEditIndex) && currentFiles && currentFiles.length) {
                const newMedia = [...media];
                const [currentFile] = currentFiles;

                if (mergedToolBarOptions!.specialOptions!.mediaOptions!.replaceEditedImage) {
                    newMedia[mediaEditIndex!] = currentFile;
                } else {
                    newMedia.splice(mediaEditIndex! + 1, 0, currentFile);
                }
                innerUploaderRef.current.updateUploadedFilesOrder(newMedia);
                setMedia(newMedia);
                if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onFilesAccepted) {
                    mergedToolBarOptions!.specialOptions!.mediaOptions!.onFilesAccepted(currentFiles, newMedia);
                }
                if (handlers && handlers!.onMediaChange) {
                    handlers!.onMediaChange(newMedia);
                }
            } else {
                setMedia(allFiles);
                if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onFilesAccepted) {
                    mergedToolBarOptions!.specialOptions!.mediaOptions!.onFilesAccepted(currentFiles, allFiles);
                }
                if (handlers && handlers!.onMediaChange) {
                    handlers!.onMediaChange(allFiles);
                }
            }
            setMediaEditIndex(null);
        };

        const handleMediaReOrder = (files: any): void => {
            setMedia(files);
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.updateUploadedFilesOrder(files);
            }
            if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaReOrder) {
                mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaReOrder(files);
            }
            if (handlers && handlers!.onMediaChange) {
                handlers!.onMediaChange(files);
            }
        };

        const handleMediaEdited = (newMediaEditIndex: number, newFile: File): void => {
            setMediaEditIndex(newMediaEditIndex);
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.addAcceptedFiles([newFile]);
            }
            if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaEdited) {
                mergedToolBarOptions!.specialOptions!.mediaOptions!.onMediaEdited(mediaEditIndex, newFile);
            }
        };

        const setEditorText = async () => {
            const { responseGeneratorOptions } = mergedToolBarOptions!.specialOptions!;

            if (responseGeneratorOptions && responseGeneratorOptions.api) {
                const text = await trackPromise(
                    responseGeneratorOptions.api(responseGeneratorOptions.params),
                    `${EditorOverlayArea}-${id}`,
                );

                editorRef?.current?.setContents(text);
            }
        };

        const handleCustomButtonClick = (widgetName: string): void => {
            switch (widgetName) {
                case defaultSpecialButtonsOptions.Template.id:
                    metadata.current.template_response = true;
                    metadata.current.reputation_vob_ai_generated_response = false;
                    metadata.current.user_generated_manual_response = false;
                    setshowTemplatePicker(!showTemplatePicker);
                    break;
                case defaultSpecialButtonsOptions.Variable.id:
                    setShowVariablePicker(!showVariablePicker);
                    break;

                case defaultSpecialButtonsOptions.Mention.id:
                    setShowMentionPicker(!showMentionPicker);
                    break;

                case defaultSpecialButtonsOptions.Emoji.id:
                    setShowEmojiPicker(!showEmojiPicker);
                    break;
                case defaultSpecialButtonsOptions.VideoRecorder.id:
                    setShowHideVideoRecord((show) => !show);
                    break;
                case defaultSpecialButtonsOptions.AudioRecorder.id:
                    setShowHideAudioRecord((show) => !show);
                    break;

                case defaultSpecialButtonsOptions.HashTags.id:
                    setShowHashTagPicker(!showHashTagPicker);
                    break;
                case defaultSpecialButtonsOptions.ResponseGenerator.id:
                    setEditorText();
                    break;
                default:
                    break;
            }
        };

        const handleReadOnly = (): void => {
            if (handlers && handlers.onReadOnlyClick) {
                handlers.onReadOnlyClick();
            }
        };

        const handleEditorClick = (): void => {
            if (!hasInteracted) {
                setHasInteracted(true);
                metadata.current.interaction_start_timestamp = new Date().toISOString();
            }
            if (handlers && handlers.onEditorClick) {
                handlers.onEditorClick();
            }
        };

        const handleChannelSelection = (index: number): void => {
            if (activeChannelIndex !== index) {
                setActiveChannelIndex(index);
                const selectedChannel = get(mergedMenuBarOptions, `responseChannelsData[${index}]`, null);

                if (selectedChannel) {
                    if (handlers?.onChannelChange) {
                        handlers!.onChannelChange(selectedChannel);
                    }
                    if (
                        !ResponseChannelsInfo[activeChannel.id].hideRichText &&
                        ResponseChannelsInfo[activeChannel.id].hideRichText !==
                            ResponseChannelsInfo[selectedChannel.id].hideRichText
                    ) {
                        editorRef.current.removeAllFormats();
                    }
                    editorRef.current.setFocus();
                }
            }
        };

        const handlePrivateCheckboxChange = (isPrivate: boolean): void => {
            setIsPrivate(isPrivate);
        };

        const handleTextChange = (textInfo: any): void => {
            if (handlers?.onTextChange) {
                handlers!.onTextChange({
                    text: textInfo.text,
                    html: textInfo.html,
                    metadata: textInfo.metadata,
                    options: {
                        responseChannel: get(activeChannel, 'id', null),
                        isPrivate,
                    },
                    media,
                });
            }
            if (validationError && textInfo?.text?.trim().length > 0) {
                setValidationError(false);
            }
        };

        const textInfoRef = useRef<any>(null);

        const handleRespond = useCallback(
            async (textInfo: any): Promise<void> => {
                if (textInfo.showWarning) {
                    setShowWarning(true);
                    textInfoRef.current = textInfo;
                    textInfoRef.current.showWarning = false;
                } else {
                    const bannedWords = await checkProfanity(textInfo.text);

                    setProfanePhrases(bannedWords);
                    if (
                        (!bannedWords || !bannedWords.length) &&
                        !isMediaUploadInProgress &&
                        (checkCharCount() || media.length)
                    ) {
                        if (handlers?.onResponseSubmit) {
                            const response = {
                                text: textInfo.text,
                                html: textInfo.html,
                                metadata: textInfo.metadata,
                                options: {
                                    responseChannel: get(activeChannel, 'id', null),
                                    isPrivate,
                                    respondButtonSplitOption: selectedOption,
                                    responseOption: responseOptionValue,
                                },
                                media,
                            };

                            metadata.current.response_publish_timestamp = new Date().toISOString();
                            const submitObj = (await handlers!.onResponseSubmit(
                                response,
                                metadata.current,
                                textInfo.evaluationResponse,
                            )) as SubmitResponse;

                            if (submitObj && submitObj.errorMsg) {
                                setEditorWarning(submitObj.errorMsg);
                            } else if (!submitObj || isUndefined(submitObj.isValid) || submitObj.isValid) {
                                resetEditor();
                            }
                        }
                    }
                }
            },
            [
                selectedOption,
                activeChannel,
                checkCharCount,
                checkProfanity,
                handlers,
                isMediaUploadInProgress,
                isPrivate,
                media,
                responseOptionValue,
            ],
        );

        const handleKeyNavigation = (keyCode: any): void => {
            if (
                has(dropdownRef, 'current.handleKeyNavigation') &&
                isFunction(dropdownRef.current.handleKeyNavigation)
            ) {
                dropdownRef.current.handleKeyNavigation({ keyCode });
            }
        };

        const handleCharCountChange = (count: number): void => {
            setCharCount(count);
        };

        const handleMeetsThresholdChanged = (newMeetsThreshold?: boolean): void => {
            if (meetsThreshold !== newMeetsThreshold) {
                setMeetsThreshold(newMeetsThreshold);
            }
        };

        const handleIsTextAnalyzedChanged = (newIsTextAnalyzed: boolean) => {
            if (isTextAnalyzed.current !== newIsTextAnalyzed) {
                isTextAnalyzed.current = newIsTextAnalyzed;
            }
        };

        const handleWidgetTrigger = ({
            displayTemplate,
            displayVariable,
            displayMention,
            displayEmoji,
            displayHashTag,
        }: {
            displayTemplate: boolean;
            displayVariable: boolean;
            displayMention: boolean;
            displayEmoji: boolean;
            displayHashTag: boolean;
        }): void => {
            setshowTemplatePicker(displayTemplate);
            setShowVariablePicker(displayVariable);
            setShowMentionPicker(displayMention);
            setShowEmojiPicker(displayEmoji);
            setShowHashTagPicker(displayHashTag);

            setReloadWidget({});
        };

        const handleMediaUploadProgress = (inProgress: boolean, progressValue: number | null): void => {
            setIsMediaUploadInProgress(inProgress);
            if (mergedToolBarOptions!.specialOptions!.mediaOptions!.onUploadProgress) {
                mergedToolBarOptions!.specialOptions!.mediaOptions!.onUploadProgress(inProgress, progressValue);
            }
        };

        const showHideVideoRecorder = (): any => {
            setShowHideVideoRecord(!showHideVideoRecord);
        };

        const hideRichText = get(ResponseChannelsInfo, `[${get(activeChannel, 'id', null)}].hideRichText`, false);
        const newToolBarOptions: ToolBarOptions = { ...mergedToolBarOptions! };

        newToolBarOptions!.respondButtonOptions!.disabled =
            (!checkCharCount() && !media.length) || isMediaUploadInProgress;
        if (newToolBarOptions!.richTextOptions!.enableRichText) {
            newToolBarOptions!.richTextOptions!.hideRichText = hideRichText;
        }
        if (activeChannel?.id === ResponseChannel.InternalNote) {
            newToolBarOptions!.respondButtonOptions!.text = t('SAVE');
        }

        const handleRecordVideo = (file: File): any => {
            setShowHideVideoRecord(false);
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.addAcceptedFiles([file]);
            }
        };

        const handleRecordAudio = (file: File): any => {
            setShowHideAudioRecord(false);
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.addAcceptedFiles([file]);
            }
        };

        const handleSuggestedResponseClick = (value: string): void => {
            handleCustomWidgetValueSelect(
                {
                    label: value,
                },
                defaultSpecialButtonsOptions.SuggestedResponse.id,
            );
        };

        const resetEditor = (): void => {
            metadata.current = initializeMetadata();
            if (editorRef && editorRef.current) {
                editorRef.current.setText('');
            }
            setMedia([]);
            setProfanePhrases([]);
            setEditorWarning('');
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.clearFiles();
            }
        };

        const handleCancelClick = (): void => {
            if (handlers?.onCancelClick) {
                handlers.onCancelClick();
                resetEditor();
            }
        };

        const handleDecision = async (publish: boolean) => {
            if (publish) {
                await handleRespond(textInfoRef.current);
            } else {
                metadata.current.publish_anyways_cancel_count += 1;
            }
            setShowWarning(false);
        };

        const handleResponseOptionSelect = (value: string): void => {
            setResponseOptionValue(value);
        };

        return (
            <div>
                {showWarning && <WarningMessage meetsThreshold={meetsThreshold} onDecision={handleDecision} />}
                <div
                    className={showWarning ? 'hide-editor' : 'r4e-response-editor'}
                    role="button"
                    tabIndex={-1}
                    onKeyPress={(): void => {
                        // Do Nothing
                    }}
                    onClick={handleEditorClick}
                >
                    {mergedReadOnlyOptions?.enable ? (
                        <ReadOnly readOnlyOptions={mergedReadOnlyOptions} handleReadOnly={handleReadOnly} t={t} />
                    ) : (
                        <div>
                            <Uploader
                                agencyId={agencyId}
                                tenantId={tenantId}
                                className={
                                    isMediaUploadInProgress &&
                                    newToolBarOptions!.specialOptions!.mediaOptions!.blockOnUpload
                                        ? 'response-editor-uploader'
                                        : ''
                                }
                                noClick
                                noDrag={newToolBarOptions!.specialOptions!.mediaOptions!.noDrag!}
                                accept={newToolBarOptions!.specialOptions!.mediaOptions!.accept}
                                maxFiles={newToolBarOptions!.specialOptions!.mediaOptions!.maxFiles}
                                minSizePerFile={newToolBarOptions!.specialOptions!.mediaOptions!.minSizePerFile!}
                                maxSizePerFile={newToolBarOptions!.specialOptions!.mediaOptions!.maxSizePerFile!}
                                maxSizeTotal={newToolBarOptions!.specialOptions!.mediaOptions!.maxSizeTotal!}
                                multiple={newToolBarOptions!.specialOptions!.mediaOptions!.multiple!}
                                disabled={newToolBarOptions!.specialOptions!.mediaOptions!.disabled!}
                                innerUploaderRef={innerUploaderRef}
                            >
                                <Overlay area={`editor-overlay-area-${id}`} />
                                <div className="editor-content-container">
                                    <MenuBar
                                        enable={mergedMenuBarOptions?.enable!}
                                        activeResponseChannelIndex={activeChannelIndex}
                                        responseChannelsData={mergedMenuBarOptions?.responseChannelsData}
                                        privateOptions={mergedMenuBarOptions?.privateOptions}
                                        handlers={{
                                            onChannelSelection: handleChannelSelection,
                                            onPrivateCheckboxChange: handlePrivateCheckboxChange,
                                        }}
                                    />
                                    <div
                                        className="response-editor-action-container"
                                        style={{
                                            position:
                                                editorRef &&
                                                editorRef.current &&
                                                isNull(editorRef.current.getSearchStartIndex())
                                                    ? 'relative'
                                                    : 'initial',
                                        }}
                                        ref={actionContainerRef}
                                    >
                                        <div
                                            className="editor-scroll"
                                            style={{
                                                position:
                                                    editorRef &&
                                                    editorRef.current &&
                                                    isNull(editorRef.current.getSearchStartIndex())
                                                        ? 'initial'
                                                        : 'relative',
                                            }}
                                        >
                                            <Editor
                                                ref={editorRef}
                                                toolBarId={toolBarId}
                                                placeholder={t(placeholder!)}
                                                defaultValue={mergedTextOptions!.defaultText!}
                                                enableRespondOnEnterKey={false}
                                                focus={focus!}
                                                onTextChange={handleTextChange}
                                                onRespond={handleRespond}
                                                onKeyNavigation={handleKeyNavigation}
                                                onCustomButtonClick={handleCustomButtonClick}
                                                onFocus={handlers?.onFocus}
                                                onBlur={handlers?.onBlur}
                                                canEdit={canEdit!}
                                                twitterSelected={mergedTextOptions!.twitterSelected}
                                                enableTemplate={
                                                    newToolBarOptions!.specialOptions!.templateOptions!.enable!
                                                }
                                                enableVariable={
                                                    newToolBarOptions!.specialOptions!.variableOptions!.enable!
                                                }
                                                enableMention={
                                                    newToolBarOptions!.specialOptions!.mentionOptions!.enable!
                                                }
                                                enableHashTag={
                                                    newToolBarOptions!.specialOptions!.hashTagOptions!.enable!
                                                }
                                                includeMentionPrefix={
                                                    newToolBarOptions!.specialOptions!.mentionOptions!.includePrefix
                                                }
                                                includeHashTagPrefix={
                                                    newToolBarOptions!.specialOptions!.hashTagOptions!.includePrefix
                                                }
                                                onMentionClick={
                                                    newToolBarOptions!.specialOptions!.mentionOptions!.handlers!.onClick
                                                }
                                                onHashTagClick={
                                                    newToolBarOptions!.specialOptions!.hashTagOptions!.handlers!.onClick
                                                }
                                                enableEmoji={newToolBarOptions!.specialOptions!.emojiOptions!.enable!}
                                                onCharCount={handleCharCountChange}
                                                onMeetsThresholdChanged={handleMeetsThresholdChanged}
                                                onWidgetTrigger={handleWidgetTrigger}
                                                backgroundColor={
                                                    (activeChannel && activeChannel.backgroundColor) ||
                                                    ((isPrivate &&
                                                        mergedMenuBarOptions?.privateOptions
                                                            ?.backgroundColor) as string)
                                                }
                                                richTextOptions={mergedToolBarOptions!.richTextOptions!}
                                                vobReviewResponseEnabled={vobReviewResponseEnabled}
                                                generateAIResponseOnLoad={generateAIResponseOnLoad}
                                                reviewCreatedAt={reviewCreatedAt}
                                                onIsTextAnalyzedChanged={handleIsTextAnalyzedChanged}
                                                onPaste={handlePaste}
                                                onManualTyping={handleManualTyping}
                                                tenantId={tenantId}
                                                reviewId={reviewId}
                                                locationId={locationId}
                                                userId={userId}
                                                userLocale={userLocale}
                                                agencyId={agencyId}
                                                loading={loading}
                                                setLoading={setLoading}
                                            />
                                            {suggestedResponses && (
                                                <div className="editor-suggested-response-container">
                                                    {map(suggestedResponses, (srItem) => (
                                                        <span
                                                            className="suggested-response-item"
                                                            role="presentation"
                                                            onClick={(): void => {
                                                                handleSuggestedResponseClick(srItem);
                                                            }}
                                                        >
                                                            {srItem}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <div
                                                className={`editor-gallery-container ${
                                                    newToolBarOptions!.specialOptions!.mediaOptions!
                                                        .displayDropContainer
                                                        ? 'drag-n-drop'
                                                        : ''
                                                } ${media.length ? 'has-data' : ''}`}
                                            >
                                                <Uploader
                                                    innerUploaderRef={innerUploaderRef}
                                                    agencyId={agencyId}
                                                    tenantId={tenantId}
                                                    media={newToolBarOptions!.specialOptions!.mediaOptions!.media}
                                                    noDrag
                                                    accept={newToolBarOptions!.specialOptions!.mediaOptions!.accept}
                                                    maxFiles={newToolBarOptions!.specialOptions!.mediaOptions!.maxFiles}
                                                    minSizePerFile={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.minSizePerFile!
                                                    }
                                                    maxSizePerFile={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.maxSizePerFile!
                                                    }
                                                    maxSizeTotal={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.maxSizeTotal!
                                                    }
                                                    multiple={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.multiple!
                                                    }
                                                    accessPolicy={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.accessPolicy!
                                                    }
                                                    moduleId={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.moduleId!
                                                    }
                                                    uploadToCloud={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.uploadToCloud
                                                    }
                                                    cancelUpload={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.cancelUpload!
                                                    }
                                                    displayProgressBar={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .displayProgressBar!
                                                    }
                                                    displayErrorModal={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .displayProgressBar!
                                                    }
                                                    shortenExternalUrl={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .shortenExternalUrl
                                                    }
                                                    onFilesAccepted={handleMediaAdded}
                                                    validateUploadedFile={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .validateUploadedFile!
                                                    }
                                                    onUploadCancel={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.onUploadCancel
                                                    }
                                                >
                                                    {newToolBarOptions!.specialOptions!.mediaOptions!
                                                        .displayDropContainer
                                                        ? newToolBarOptions!.specialOptions!.mediaOptions!
                                                              .dropContainerInfoElement || (
                                                              <div className="drag-label">
                                                                  {t('DROP_FILES_TO_ATTACH')}
                                                              </div>
                                                          )
                                                        : null}
                                                </Uploader>
                                                <ProfanityWarning
                                                    profanePhrases={profanePhrases}
                                                    t={t}
                                                    CustomWarningElement={profanityOptions?.CustomWarningElement}
                                                />
                                                {editorWarning && <EditorWarning warningText={editorWarning} />}
                                                <Gallery
                                                    galleryData={media}
                                                    canDelete={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.canDelete
                                                    }
                                                    displayFileNameOnHover={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .displayFileNameOnHover!
                                                    }
                                                    openModalOnMediaClick={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .openModalOnMediaClick!
                                                    }
                                                    openModalOnHandleIconClick={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .openModalOnHandleIconClick!
                                                    }
                                                    canReOrder={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.canReOrder
                                                    }
                                                    canEditImage={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.canEditImage
                                                    }
                                                    onDelete={handleMediaRemove}
                                                    onMediaClick={handleMediaClick}
                                                    onMediaReOrder={handleMediaReOrder}
                                                    onMediaEdited={handleMediaEdited}
                                                    onMediaAltTextClick={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!
                                                            .onMediaAltTextClick
                                                    }
                                                    selectedSources={
                                                        newToolBarOptions!.specialOptions!.mediaOptions!.selectedSources
                                                    }
                                                />
                                            </div>
                                            {showTemplatePicker ||
                                            showVariablePicker ||
                                            showMentionPicker ||
                                            showHashTagPicker ||
                                            showEmojiPicker ? (
                                                <Widgets
                                                    agencyId={agencyId}
                                                    tenantId={tenantId}
                                                    dropdownRef={dropdownRef}
                                                    actionContainerRef={actionContainerRef}
                                                    editorRef={editorRef}
                                                    handleCustomWidgetValueSelect={handleCustomWidgetValueSelect}
                                                    handleCustomWidgetClose={handleCustomWidgetClose}
                                                    handleDataLoad={handleWidgetDataLoad}
                                                    showTemplatePicker={showTemplatePicker}
                                                    showVariablePicker={showVariablePicker}
                                                    showMentionPicker={showMentionPicker}
                                                    showEmojiPicker={showEmojiPicker}
                                                    showHashTagPicker={showHashTagPicker}
                                                    widgetPlacement={widgetPlacement!}
                                                    toolBarOptions={newToolBarOptions}
                                                />
                                            ) : null}
                                            {toolBarOptions?.specialOptions?.videoRecordOptions?.enable &&
                                            showHideVideoRecord ? (
                                                <VideoRecorder
                                                    onVideoRecord={handleRecordVideo}
                                                    closeVideoRecorder={showHideVideoRecorder}
                                                    maxTimeLimit={
                                                        toolBarOptions?.specialOptions?.videoRecordOptions?.maxTimeLimit
                                                    }
                                                    showCounterBeforeRecord
                                                    countdownTimer={3}
                                                />
                                            ) : null}
                                            {mergedToolBarOptions?.specialOptions?.audioRecordOptions?.enable &&
                                            showHideAudioRecord ? (
                                                <AudioRecorder
                                                    onClose={(): void => setShowHideAudioRecord(false)}
                                                    onRecord={handleRecordAudio}
                                                    maxTimeLimit={
                                                        mergedToolBarOptions?.specialOptions?.audioRecordOptions
                                                            ?.maxTimeLimit
                                                    }
                                                    showCounterBeforeRecord
                                                    countdownTimer={3}
                                                />
                                            ) : null}
                                        </div>
                                        <ToolBar
                                            id={toolBarId}
                                            agencyId={agencyId}
                                            tenantId={tenantId}
                                            toolBarOptions={newToolBarOptions}
                                            textOptions={mergedTextOptions}
                                            charCount={charCount}
                                            innerUploaderRef={innerUploaderRef}
                                            widgetPlacement={widgetPlacement}
                                            handlers={{
                                                onRespondButtonClick: callRespond,
                                                onFilesAccepted: handleMediaAdded,
                                                onMediaUploadProgress: handleMediaUploadProgress,
                                                onCancelButtonClick: handleCancelClick,
                                                onSplitButtonSelect: setSelectedOption,
                                                onGenerateAIResponseButtonClick: callGenerate,
                                                onAnalyzeResponseButtonClick: callAnalyze,
                                                onResponseOptionSelect: handleResponseOptionSelect,
                                            }}
                                            vobReviewResponseEnabled={vobReviewResponseEnabled}
                                            isTextAnalyzed={isTextAnalyzed.current}
                                            validationError={validationError}
                                            showResponseOptions={showResponseOptions}
                                            loading={loading}
                                            isNorthStarEnabled={isNorthStarEnabled}
                                        />
                                    </div>
                                </div>
                                {!canEdit && <div className="editor-disabled-mode" />}
                            </Uploader>
                            {isMediaUploadInProgress &&
                            newToolBarOptions!.specialOptions!.mediaOptions!.blockOnUpload ? (
                                <div className="response-editor-upload-block" />
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

ResponseEditor.defaultProps = {
    id: '1',
    tenantId: -1,
    agencyId: -1,
    placeholder: 'DEFAULT_PLACEHOLDER',
    readOnlyOptions: {
        enable: false,
        icon: 'icon-lock',
        text: 'WIDGET_LOCKED_TEXT',
        isClickable: false,
        backgroundColor: '',
    },
    textOptions: {
        defaultText: [],
        displayCharCount: true,
        minCharLimit: null,
        maxCharLimit: null,
        displayMaxChar: false,
    },
    menuBarOptions: {
        enable: false,
        privateOptions: {
            enable: false,
            label: 'PRIVATE',
            tooltip: '',
            checked: false,
            backgroundColor: Color.Turquoise7,
            locked: false,
        },
        responseChannelsData: [],
    },
    toolBarOptions: {
        enable: true,
        richTextOptions: {
            enableRichText: false,
            options: {
                enableBold: true,
                enableItalic: true,
                enableUnderline: true,
                enableStrikeThrough: true,
                enableBlockQuote: true,
                enableCodeBlock: true,
                enableOrderedList: true,
                enableBulletList: true,
                enableLink: true,
            },
        },
        specialOptions: {
            videoRecordOptions: {
                enable: false,
            },
            audioRecordOptions: {
                enable: false,
            },
            templateOptions: {
                enable: false,
            },
            variableOptions: {
                enable: false,
            },
            mentionOptions: {
                enable: false,
                includePrefix: true,
                handlers: {},
            },
            emojiOptions: {
                enable: false,
            },
            mediaOptions: {
                enable: false,
                uploadToCloud: true,
                displayProgressBar: true,
                displayDropContainer: false,
                replaceEditedImage: false,
            },
            hashTagOptions: {
                enable: false,
                includePrefix: true,
                handlers: {},
            },
            responseGeneratorOptions: {
                enable: false,
            },
        },
        respondButtonOptions: {
            enable: true,
            text: 'RESPOND',
            disabled: false,
            type: RespondButtonType.Button,
        },
        cancelButtonOptions: {
            enable: false,
            text: 'CANCEL',
            disabled: false,
        },
    },
    enableRespondOnEnterKey: true,
    widgetPlacement: ResponseEditorWidgetPlacement.Bottom,
    focus: true,
    canEdit: true,
    vobReviewResponseEnabled: false,
};
